import { format, addDays } from 'date-fns';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { ProductCreate } from '../../Inventory/Product';

import { EstimateAndQuoteCreateCustomerSelect } from './EstimateAndQuoteCreateCustomerSelect';

import { Box, BoxColumn, BoxRowColumn } from '@components/Box';
import { CollapsibleBoxColumn } from '@components/CollapsibleBoxColumn';
import { FormSectionContainer } from '@components/Container';
import { CustomerDisplayCard } from '@components/CustomerDisplayCard';
import { ExpandingBoxColumn } from '@components/ExpandingBoxColumn';
import {
  FormikDateInput,
  FormikSelect,
  FormikTextArea,
} from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { FormActionsBar } from '@components/FormActionsBar';
import { LineItemsTable } from '@components/LineItemsTable';
import { Modal } from '@components/Modal';
import { routes } from '@config/routes';
import { useCustomerData } from '@hooks/useCustomerData';
import { useFormSubmission } from '@hooks/useFormSubmission';
import { useLineItems } from '@hooks/useLineItems';
import { useNotification } from '@hooks/useNotification';
import { useProductData } from '@hooks/useProductData';
import { useSalesTaxEffectiveRates } from '@hooks/useSalesTaxEffectiveRates';
import { useTaxCalculation } from '@hooks/useTaxCalculation';
import {
  estimateAndQuoteDefaultValue,
  estimateAndQuoteValidation,
} from '@models/EstimateAndQuote';
import {
  estimateAndQuoteDetailDefaultValue,
  EstimateAndQuoteDetailViewModel,
} from '@models/EstimateAndQuoteDetail';
import {
  estimateAndQuoteDetailSalesTaxDefaultValue,
  EstimateAndQuoteDetailSalesTaxViewModel,
} from '@models/EstimateAndQuoteDetailSalesTax';
import { EstimateAndQuoteService } from '@services/EstimateAndQuote';
import { ProductService } from '@services/Product';

export const EstimateAndQuoteCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showInfo, showSuccess, showError } = useNotification();
  const previousLocation = location.state?.from || routes.RNC_ESTIMATES;

  // ===== SHARED HOOKS =====
  // Customer data hook - manages customer, contacts, addresses
  const {
    selectedCustomer,
    customerContacts,
    customerBillingAddresses,
    customerShippingAddresses,
    loadSelectedCustomer,
    loadSelectedContact,
  } = useCustomerData({
    onError: showError,
    onCustomerLoaded: (customer) => {
      // Recalculate tax amounts when customer is loaded
      recalculateTaxAmountsForCustomer(customer.isTaxExempt === true);
    },
    onCustomerCleared: () => {
      // Recalculate tax amounts when customer is cleared (non-exempt)
      recalculateTaxAmountsForCustomer(false);
    },
  });

  // Product data hook - manages product list
  const { products, loadProducts, setProducts } = useProductData({
    onError: showError,
  });

  // Tax calculation hook - manages tax exempt status
  const { setFormikPropsRef, getIsTaxExempt } = useTaxCalculation();

  // Sales tax effective rates
  const {
    salesTaxes,
    salesTaxEffectiveRates,
    loadData: loadSalesTaxEffectiveRates,
  } = useSalesTaxEffectiveRates();

  // Line items hook - manages estimate details
  const {
    lineItems: estimateDetails,
    setLineItems: setEstimateDetails,
    addLineItem: handleAddEstimateDetail,
    removeLineItem: handleRemoveEstimateDetail,
    resetLineItem: handleResetEstimateDetail,
    updateLineItemQuantity,
    updateLineItemSalesTax,
    updateSalesTaxRates,
    recalculateTaxAmounts,
    getSalesTaxesBySalesTaxID,
  } = useLineItems<
    EstimateAndQuoteDetailViewModel,
    EstimateAndQuoteDetailSalesTaxViewModel
  >(
    {
      defaultLineItem: estimateAndQuoteDetailDefaultValue,
      defaultSalesTax: estimateAndQuoteDetailSalesTaxDefaultValue,
      salesTaxesKey: 'estimateDetailSalesTaxes',
    },
    { onInfo: showInfo }
  );

  // ===== LOCAL STATE =====
  const [showCreateProduct, setShowCreateProduct] = useState(false);

  // Form submission hook
  const { setSubmitType, handlePostSubmitNavigation } = useFormSubmission({
    listRoute: routes.RNC_ESTIMATES,
    getEditRoute: (id) => `${routes.RNC_ESTIMATES}/${id}/edit`,
  });

  const [currentDate] = useState(() => {
    return format(new Date(), 'MM/dd/yyy');
  });
  const [validUntil] = useState(() => {
    const newDate = addDays(new Date(), 30);
    return format(newDate, 'MM/dd/yyyy');
  });

  // State for collapsible left column
  const [isLeftColumnCollapsed, setIsLeftColumnCollapsed] = useState(false);

  // ===== TAX RECALCULATION =====
  // Use the hook's recalculateTaxAmounts method
  const recalculateTaxAmountsForCustomer = recalculateTaxAmounts;

  // ===== PRODUCT SELECTION =====
  const loadSelectedProduct = (
    productId: string,
    index: number,
    formikProps?: any
  ) => {
    const emptyEstimateDetail: EstimateAndQuoteDetailViewModel =
      estimateAndQuoteDetailDefaultValue;
    if (productId === '0') {
      const updatedEstimateDetails = [...estimateDetails];
      updatedEstimateDetails[index] = emptyEstimateDetail;
      setEstimateDetails(updatedEstimateDetails);
    } else {
      ProductService.getById(Number(productId)).then((result) => {
        if (result.isSuccess && 'data' in result) {
          const isTaxExempt =
            formikProps?.values?.isTaxExempt ??
            selectedCustomer?.isTaxExempt === true;

          const estimateDetailSalesTaxes = result.data.productSalesTaxes
            .map((o) => {
              const salesTaxEffectiveRate = salesTaxEffectiveRates.find(
                (i) => i.id == o.salesTaxId
              );
              return {
                salesTaxID: o.salesTaxId,
                salesTaxAbbreviation: o.salesTax.abbreviation,
                rate: salesTaxEffectiveRate ? salesTaxEffectiveRate.rate : 0,
                salesTaxAmount: isTaxExempt
                  ? 0
                  : (Number(
                      salesTaxEffectiveRate ? salesTaxEffectiveRate.rate : 0
                    ) *
                      Number(result.data.price)) /
                    100,
              } as EstimateAndQuoteDetailSalesTaxViewModel;
            })
            .concat([estimateAndQuoteDetailSalesTaxDefaultValue]);

          const newEstimateDetail: EstimateAndQuoteDetailViewModel = {
            id: 0,
            estimateID: 0,
            productID: result.data.id,
            product: result.data,
            description: result.data.description,
            quantity: 1,
            price: result.data.price,
            estimateDetailSalesTaxes: estimateDetailSalesTaxes,
            subTotal: result.data.price,
          };

          const updatedEstimateDetails = [...estimateDetails];
          updatedEstimateDetails[index] = newEstimateDetail;
          setEstimateDetails(updatedEstimateDetails);
        } else {
          const errorMessage = `Failed to get the selected product details. ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  // Wrapper to adapt hook's updateLineItemQuantity to existing signature
  const handleUpdateEstimateDetailQuantity = (
    index: number,
    quantity: number
  ) => {
    updateLineItemQuantity(index, quantity, getIsTaxExempt());
  };

  // Handle updating sales tax on a line item
  const handleUpdateEstimateDetailSalesTax = (
    estimateDetailIndex: number,
    estimateDetailSalesTaxIndex: number,
    _estimateDetailSalesTaxesLength: number,
    salesTaxID: number
  ) => {
    const salesTax = salesTaxEffectiveRates.find((o) => o.id == salesTaxID);
    updateLineItemSalesTax(
      estimateDetailIndex,
      estimateDetailSalesTaxIndex,
      salesTax,
      getIsTaxExempt()
    );
  };

  // Use hook's updateSalesTaxRates for updating tax rates
  const handleUpdateEstimateDetailSalesTaxRates = () => {
    updateSalesTaxRates(salesTaxEffectiveRates, getIsTaxExempt());
  };

  useEffect(() => {
    handleUpdateEstimateDetailSalesTaxRates();
  }, [salesTaxEffectiveRates]);

  useEffect(() => {
    loadProducts();
    loadSalesTaxEffectiveRates(new Date());
  }, []);

  return (
    <>
      <Box>
        <div className="pt-2 lg:pb-6 md:pb-4 pb-2">
          <p className="w-full text-center lg:text-2xl md:text-lg text-md font-bold text-primary-900">
            Create Estimate
          </p>
        </div>
        <Formik
          initialValues={{
            ...estimateAndQuoteDefaultValue,
            date: currentDate,
            dueDate: validUntil,
          }}
          validationSchema={estimateAndQuoteValidation}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values, actions) => {
            const formattedValues = {
              ...values,
              date: format(new Date(values.date), 'yyyy-MM-dd'),
              dueDate: format(new Date(values.dueDate), 'yyyy-MM-dd'),
              discountType: Number(values.discountType) || 1,
            };

            formattedValues.estimateDetails = estimateDetails;
            try {
              EstimateAndQuoteService.create(formattedValues)
                .then((result) => {
                  if (result.isSuccess) {
                    const successMessage = `Successfully created the estimate and quote record.`;
                    showSuccess(successMessage);

                    if ('data' in result && result.data?.id) {
                      handlePostSubmitNavigation({
                        success: true,
                        id: result.data.id,
                      });
                    }
                  } else {
                    const errorMessage = `Failed to create the estimate and quote record. ${result.errorMessages}`;
                    console.error(errorMessage);
                    showError(errorMessage);
                  }
                })
                .catch((error) => {
                  console.error(error);
                  showError('An unexpected error occurred while updating.');
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            } catch (error) {
              console.error(error);
              actions.setSubmitting(false);
            }
          }}
        >
          {(formikProps) => {
            // Set formik props ref for access in tax calculation hook
            setFormikPropsRef(formikProps);

            return (
              <form method="POST" onSubmit={formikProps.handleSubmit}>
                <FormSectionContainer>
                  {/* Calling Card - Only visible when left column is collapsed */}
                  {isLeftColumnCollapsed && selectedCustomer && (
                    <CustomerDisplayCard
                      selectedCustomer={selectedCustomer}
                      contactID={formikProps.values.contactID}
                      contactEmail={formikProps.values.contactEmail}
                      customerContacts={customerContacts}
                      onEditClick={() => setIsLeftColumnCollapsed(false)}
                    />
                  )}

                  <BoxRowColumn>
                    <CollapsibleBoxColumn
                      isCollapsed={isLeftColumnCollapsed}
                      onToggleCollapse={() =>
                        setIsLeftColumnCollapsed(!isLeftColumnCollapsed)
                      }
                      canCollapse={!!selectedCustomer}
                      collapseTitle="Collapse form"
                      disabledTitle="Please select a customer first"
                    >
                      <EstimateAndQuoteCreateCustomerSelect
                        preSelectedCustomerId={
                          selectedCustomer?.id?.toString() ||
                          formikProps.values.customerID?.toString()
                        }
                        onCustomerSelectChange={(customerId: string) => {
                          loadSelectedCustomer(customerId, formikProps);
                        }}
                      />

                      {selectedCustomer && (
                        <div className="mb-4">
                          <label className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={formikProps.values.isTaxExempt || false}
                              onChange={(e) => {
                                formikProps.setFieldValue(
                                  'isTaxExempt',
                                  e.target.checked
                                );
                                // Recalculate tax amounts when tax exemption status changes
                                setTimeout(() => {
                                  recalculateTaxAmountsForCustomer(
                                    e.target.checked
                                  );
                                }, 0);
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="font-medium text-gray-700 text-xs md:text-sm">
                              Tax Exempt
                            </span>
                          </label>
                        </div>
                      )}

                      <FormikInput label="Customer Ref" name="customerRef" />

                      <FormikSelect
                        label="Contact Name"
                        name="contactID"
                        selection={customerContacts}
                        onChange={(
                          event: React.ChangeEvent<HTMLSelectElement>
                        ) => {
                          loadSelectedContact(event.target.value, formikProps);
                        }}
                      />

                      <BoxRowColumn>
                        <BoxColumn>
                          <FormikInput
                            label="Email"
                            name="contactEmail"
                            readonly={true}
                          />
                        </BoxColumn>

                        <BoxColumn>
                          <FormikInput
                            label="Phone"
                            name="contactPhone"
                            readonly={true}
                          />
                        </BoxColumn>
                      </BoxRowColumn>

                      <BoxRowColumn>
                        <BoxColumn>
                          <FormikDateInput
                            label="Date (mm/dd/yyyy)"
                            name="date"
                            type="date"
                            changeHandler={(value: string) => {
                              loadSalesTaxEffectiveRates(new Date(value));
                            }}
                          />
                        </BoxColumn>

                        <BoxColumn>
                          <FormikDateInput
                            label="Due Date (mm/dd/yyyy)"
                            name="dueDate"
                            type="date"
                            onChange={(e: any) =>
                              formikProps.setFieldValue(
                                'dueDate',
                                format(new Date(e.target.value), 'MM/dd/yyyy')
                              )
                            }
                          />
                        </BoxColumn>
                      </BoxRowColumn>

                      <FormikSelect
                        label="Billing Address"
                        name="billingAddressID"
                        selection={customerBillingAddresses}
                      />

                      {!formikProps.values.isSameShipping ? (
                        <FormikSelect
                          label="Shipping Address"
                          name="shippingAddressID"
                          selection={customerShippingAddresses}
                        />
                      ) : (
                        <>
                          <label className="block lg:text-sm md:text-sm text-xs font-medium text-primary-700">
                            Shipping Address
                          </label>
                          <i>Same as Billing Address</i>
                        </>
                      )}

                      <FormikTextArea label="Notes" name="notes" />
                    </CollapsibleBoxColumn>

                    <ExpandingBoxColumn isExpanded={isLeftColumnCollapsed}>
                      <div className="py-4">
                        <LineItemsTable<
                          EstimateAndQuoteDetailViewModel,
                          EstimateAndQuoteDetailSalesTaxViewModel
                        >
                          lineItems={estimateDetails}
                          products={products}
                          salesTaxes={salesTaxes || []}
                          salesTaxesKey="estimateDetailSalesTaxes"
                          onProductSelect={(productId, index) =>
                            loadSelectedProduct(productId, index, formikProps)
                          }
                          onQuantityChange={handleUpdateEstimateDetailQuantity}
                          onSalesTaxChange={handleUpdateEstimateDetailSalesTax}
                          onAddLineItem={handleAddEstimateDetail}
                          onRemoveLineItem={handleRemoveEstimateDetail}
                          onCreateProductClick={() =>
                            setShowCreateProduct(true)
                          }
                          getSalesTaxesBySalesTaxID={() =>
                            getSalesTaxesBySalesTaxID
                          }
                          formikValues={{
                            isTaxExempt: formikProps.values.isTaxExempt,
                            discountType: Number(
                              formikProps.values.discountType
                            ),
                            discount: formikProps.values.discount,
                          }}
                        />
                      </div>

                      <div className="flex items-end justify-end h-full">
                        <FormActionsBar
                          onCancel={() => navigate(previousLocation)}
                          isSubmitting={formikProps.isSubmitting}
                          isValid={formikProps.isValid}
                          buttons={[
                            {
                              label: 'Save & Close',
                              submitType: 'saveAndClose',
                            },
                            { label: 'Save', submitType: 'save' },
                          ]}
                          onSubmitTypeChange={setSubmitType}
                        />
                      </div>
                    </ExpandingBoxColumn>
                  </BoxRowColumn>
                </FormSectionContainer>
              </form>
            );
          }}
        </Formik>
      </Box>
      {showCreateProduct && (
        <Modal show={true}>
          <ProductCreate
            onClose={(productResultViewModel) => {
              setShowCreateProduct(false);

              if (productResultViewModel && productResultViewModel.data) {
                const newProducts = [
                  ...products.slice(0, products.length - 1),
                  {
                    value: productResultViewModel.data.id.toString(),
                    text: productResultViewModel.data.name,
                  },
                ];

                setProducts(newProducts);

                loadSelectedProduct(
                  productResultViewModel.data.id.toString(),
                  estimateDetails.length - 1
                );
              } else {
                handleResetEstimateDetail(estimateDetails.length - 1);
              }
            }}
          />
        </Modal>
      )}
    </>
  );
};
